import { Router } from "@frui.ts/screens";
import { computed, reaction } from "mobx";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import EventBus from "services/eventBus";
import type CountrySelectViewModel from "../../../countrySelectViewModel";
import type PayGateRefundResponse from "entities/payGateRefundResponse";
import PayGateRefundsSearchFilter, {
  PayGateRefundsSearchFilterValidationRules,
} from "entities/filters/refunds/payGateRefundsSearchFilter";
import { attachAutomaticValidator } from "@frui.ts/validation";
import PayGateRefundDetailViewModel from "./payGateRefundDetailViewModel";
import { bound } from "@frui.ts/helpers/dist";
import RefundType from "entities/refundType";
import RefundRepository from "repositories/refundRepository";
import OptionsService from "services/optionsService";
import PayGateRefundPaymentTemplate from "entities/payGateRefundPaymentTemplate";
import RefundApprovalType from "entities/refundApprovalType";
import RefundsApprovalService from "services/refundsApprovalService";
import RefundApprovalsListViewModel from "../refundApprovalsListViewModel";
import Selectable, * as selectableListViewModel from "../../../selectableListViewModel";
import { emptyValue } from "utils/emptyValue";

const navigationName = "paygate";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
  children: [PayGateRefundDetailViewModel],
})
@Selectable
export default class PayGateRefundsViewModel extends RefundApprovalsListViewModel<
  PayGateRefundResponse,
  PayGateRefundDetailViewModel,
  PayGateRefundsSearchFilter
> {
  readonly modelName = "payGateRefund";
  readonly refundType = RefundType.PayGate;
  readonly approvalType = RefundApprovalType;

  @computed
  get paygateRefundPaymentTemplatesOptions() {
    return this.optionsService.entityOptions(PayGateRefundPaymentTemplate);
  }

  constructor(
    protected detailViewModelFactory: ReturnType<typeof PayGateRefundDetailViewModel.Factory>,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected repository: RefundRepository,
    protected approvalService: RefundsApprovalService,
    protected optionsService: OptionsService,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super(localizationService, eventBus, approvalService, countrySelectViewModelFactory);
    this.navigationName = navigationName;
  }

  @computed
  get self(): selectableListViewModel.SelectableListViewModel<PayGateRefundsViewModel, PayGateRefundResponse> {
    return this as any;
  }

  protected processData(items: PayGateRefundResponse[]) {
    items.forEach(item => {
      item.isSelectable =
        item.permittedActions.approval.isAllowed && (this.canApprove(item) || false) && this.myApprovalStep(item);

      return item;
    });

    return items;
  }

  onInitialize() {
    this.unsubscriptions.push(
      reaction(
        () => [
          this.filter.arn,
          this.filter.authorizedDate,
          this.filter.cardNumber,
          this.filter.countryId,
          this.filter.invoiceNumber,
          this.filter.licensePlate,
          this.filter.paidAmount,
          this.filter.refundAmount,
          this.filter.refundPaymentTemplateId,
          this.filter.payGatePaymentIdent,
          this.filter.terminalId,
          this.filter.variableSymbol,
        ],
        () => {
          this.filter.fullTextSearchEnabled = false;
        }
      )
    );
    return super.onInitialize();
  }

  @bound
  onItemClick() {
    return (item: PayGateRefundResponse) => this.tryActivateChild(this.detailViewModelFactory(item.id, this.pagingFilter));
  }

  protected fetchData() {
    const { limit, offset } = this.pagingFilter;

    this.repository
      .getAllPayGateRefunds({ limit: 1, offset: 0 }, { approvedBy: RefundApprovalType.ChiefAccountant })
      .then(response => {
        if (response.items.length > 0) {
          this.isAnyExportable = true;
        }
      });

    return this.repository.getAllPayGateRefunds({ offset, limit }, this.appliedFilter);
  }

  protected createFilter() {
    return attachAutomaticValidator(new PayGateRefundsSearchFilter(), PayGateRefundsSearchFilterValidationRules);
  }

  translateRefundReason(refundPaymentTemplateId: string) {
    return (
      this.optionsService.entityOptions(PayGateRefundPaymentTemplate).find(option => option.id === refundPaymentTemplateId)
        ?.label ?? emptyValue
    );
  }

  async findNavigationChild(navigationName?: string) {
    if (navigationName && this.allowWrite) {
      return this.detailViewModelFactory(navigationName, this.pagingFilter);
    }

    return Promise.resolve(undefined);
  }

  @bound
  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `refundApprovals.payGate.${code}`, size);
  }
}
